import { useForm } from "react-hook-form";
import { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import toast from "react-hot-toast";
import Form from "react-bootstrap/Form";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import TextField from "@mui/material/TextField";

import CardTemplate from "./template/CardTemplate";
import BtnSpinner from "../../../components/btnSpinner";
import { theme } from "../../../theme";
import "./SetExpiryDate.css";
import {
    fetchDocumentsExpiryDateAndComment,
    updateExpiryDocument,
} from "../service";

const SetExpiryDate = forwardRef(({
    document_id,
    document_type,
    document_types_is_expiry_date_mandatory = false,
}, ref) => {
    const {
        register,
        handleSubmit,
        reset,
        getValues,
        formState: { errors },
    } = useForm();

    const [loading, setLoading] = useState(false);

    const formatDateForInput = useCallback(
        (dateStr) => (dateStr ? new Date(dateStr).toISOString().split("T")[0] : ""),
        []
    );

    const loadData = useCallback(async () => {
        if (!document_id) return;
        setLoading(true);
        try {
            const res = await fetchDocumentsExpiryDateAndComment(
                document_id,
                document_type
            );
            if (res.status === "Success") {
                const d = res.expiryDateAndComment;
                reset({
                    expiry_date: formatDateForInput(d?.expiry_date),
                    comment: d?.comment || "",
                });
            } else {
                toast.error(res.message);
            }
        } catch (err) {
            console.error("Error fetching expiry date and comment:", err);
            toast.error("Failed to load expiry date");
        } finally {
            setLoading(false);
        }
    }, [document_id, document_type, reset, formatDateForInput]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useImperativeHandle(ref, () => ({
        async saveExpiryData() {
            const formData = getValues();

            if (document_types_is_expiry_date_mandatory && !formData.expiry_date) {
                toast.error("Expiry date is required before approving");
                return false;
            }

            formData?.expiry_date && await updateExpiryDate(formData);
            return true;
        }
    }));

    const updateExpiryDate = async (formData) => {
        setLoading(true);
        try {
            const res = await updateExpiryDocument({
                document_id,
                document_type,
                comment: formData.comment,
                expiry_date: new Date(formData.expiry_date).getTime(),
            });
            res.status === "Success" ? toast.success(res.message) : toast.error(res.message);
        } catch (err) {
            console.error("onSubmit error:", err);
            toast.error("Failed to update expiry date");
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (formData) => await updateExpiryDate(formData);

    return (
        <CardTemplate
            showToggleBtn={false}
            isShowMandatoryIcon={document_types_is_expiry_date_mandatory}
            isBoxShadow
            isCenterTitle
            spanTitle="Set Expiry Date"
        >
            <Form onSubmit={handleSubmit(onSubmit)} className="set-expiry-date-form">
                <FormControl error={!!errors.expiry_date} fullWidth>
                    <strong>
                        Expiry date
                        {document_types_is_expiry_date_mandatory && (
                            <i className={`material-icons opacity-10 ms-2 text-danger text-sm`}>
                                star
                            </i>
                        )}
                    </strong>
                    <TextField
                        type="date"
                        size="small"
                        placeholder="Expiry date"
                        error={!!errors.expiry_date}
                        {...register("expiry_date", {
                            required:  "Expiry date is required"
                        })}
                    />
                    {errors.expiry_date && (
                        <FormHelperText>{errors.expiry_date.message}</FormHelperText>
                    )}
                </FormControl>

                <FormControl fullWidth className="mt-3">
                    <strong>Comment</strong>
                    <TextField
                        placeholder="Comment"
                        multiline
                        rows={4}
                        {...register("comment")}
                    />
                </FormControl>

                <div className="text-center mt-3">
                    <button
                        type="submit"
                        className={`btn btn-${theme.main} w-100 btn-sm mb-0`}
                        disabled={loading}
                    >
                        {loading ? <BtnSpinner /> : "Save"}
                    </button>
                </div>
            </Form>
        </CardTemplate>
    );
});

export default SetExpiryDate;